import { isRedactedInlineAttachmentUrl } from '@/lib/shared/chat-attachments/redacted-inline-attachment'
import { fetchWithRetry } from '@/lib/shared/network'

import { isCodeAttachmentPart } from './html-attachment-utils'

/**
 * Hard cap on per-file inlined text content (in characters) before it is
 * truncated and tagged with a "[truncated]" footer. Picked to comfortably
 * fit a typical .md / .csv / .json upload while staying under ~15K input
 * tokens per file.
 */
export const MAX_INLINED_TEXT_FILE_CHARS = 60_000

/**
 * Cumulative cap on inlined text content per user message. Files past this
 * budget fall back to a one-line summary so a single message with many
 * large attachments cannot blow up the model context.
 */
export const MAX_INLINED_TEXT_TOTAL_CHARS = 200_000

type AttachmentLikePart = {
  filename?: string
  mediaType?: string
  url?: string
}

/**
 * Curated allow-list of `application/*` MIME types that carry UTF-8 text we
 * can safely inline as a fenced code block. Everything matching `text/*`
 * is accepted unconditionally elsewhere — this list adds the
 * non-`text/*`-but-still-text exceptions (JSON, YAML, JS, etc.).
 */
const TEXTUAL_APPLICATION_MIME_TYPES = new Set([
  'application/json',
  'application/jsonl',
  'application/ld+json',
  'application/x-ndjson',
  'application/xml',
  'application/xhtml+xml',
  'application/yaml',
  'application/x-yaml',
  'application/javascript',
  'application/ecmascript',
  'application/typescript',
  'application/x-typescript',
  'application/sql',
  'application/graphql',
  'application/toml',
  'application/x-sh',
  'application/x-shellscript',
  'application/x-www-form-urlencoded',
  'application/x-perl',
  'application/x-python',
  'application/x-ruby',
  // A saved email message (`.eml`) — imported deterministically, inlined as text.
  'message/rfc822',
])

/**
 * Curated allow-list of file extensions that we treat as readable text. Used
 * as a fallback when the upload pipeline strips or mis-stamps the MIME type
 * (e.g. some browsers report `.md` as `application/octet-stream`).
 */
const TEXT_FILE_EXTENSIONS = new Set([
  // Docs / data
  'md',
  'markdown',
  'mdx',
  'txt',
  'log',
  'csv',
  'tsv',
  'psv',
  'rst',
  // Structured data
  'json',
  'jsonl',
  'jsonc',
  'ndjson',
  'yml',
  'yaml',
  'toml',
  'ini',
  'env',
  'conf',
  'cfg',
  'properties',
  // Markup
  'xml',
  'html',
  'htm',
  'mjml',
  'eml',
  'svg',
  'rss',
  'atom',
  // Styles
  'css',
  'scss',
  'sass',
  'less',
  'styl',
  'postcss',
  // Web / scripting source
  'ts',
  'tsx',
  'js',
  'jsx',
  'mjs',
  'cjs',
  'vue',
  'svelte',
  'astro',
  // General source
  'py',
  'pyi',
  'rb',
  'go',
  'rs',
  'java',
  'kt',
  'kts',
  'scala',
  'c',
  'cc',
  'cpp',
  'cxx',
  'h',
  'hpp',
  'hxx',
  'm',
  'mm',
  'cs',
  'fs',
  'vb',
  'php',
  'swift',
  'dart',
  'lua',
  'pl',
  'pm',
  // Shells / batch
  'sh',
  'bash',
  'zsh',
  'fish',
  'ps1',
  'bat',
  'cmd',
  // Schemas / queries
  'sql',
  'gql',
  'graphql',
  'prisma',
  'proto',
  // Infra
  'dockerfile',
  'makefile',
  'tf',
  'hcl',
  'nix',
])

function getExtension(filename: string | undefined): string | undefined {
  if (!filename) {
    return
  }
  const lower = filename.toLowerCase().trim()
  if (!lower) {
    return
  }
  // Special-case extensionless filenames that conventionally hold text.
  const base = lower.split('/').pop() ?? lower
  if (base === 'dockerfile' || base === 'makefile' || base === '.env') {
    return base.replace(/^\./, '')
  }
  const dotIndex = base.lastIndexOf('.')
  if (dotIndex < 0 || dotIndex === base.length - 1) {
    return
  }
  return base.slice(dotIndex + 1)
}

function urlLooksLikeTextDataUrl(url: string | undefined): boolean {
  if (!url) {
    return false
  }
  return /^data:(?:text\/|application\/(?:json|xml|yaml|javascript|typescript|graphql|toml|x-sh|x-www-form-urlencoded|jsonl|ld\+json|x-ndjson))/i.test(
    url
  )
}

export const DOCX_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

/**
 * Returns true when an attachment part is a Word document (.docx). DOCX is
 * a zip container, NOT text — it must never match
 * {@link isTextFileAttachmentPart} (reading it via `response.text()` would
 * mojibake), and instead goes through the mammoth-based reader in
 * `app/api/orchestrator/read-docx-attachment.ts`.
 */
export function isDocxAttachmentPart(part: AttachmentLikePart): boolean {
  const mediaType = part.mediaType?.toLowerCase().trim() ?? ''
  if (mediaType === DOCX_MEDIA_TYPE) {
    return true
  }
  return getExtension(part.filename) === 'docx'
}

/**
 * Returns true when an attachment part is plausibly a text document we can
 * read as UTF-8 and inline into the model prompt. Strict superset of
 * {@link isCodeAttachmentPart} so existing call sites that gate on "is this
 * a code attachment" can swap to this predicate without losing coverage.
 */
export function isTextFileAttachmentPart(part: AttachmentLikePart): boolean {
  // DOCX must stay disjoint: its MIME contains "openxmlformats", which the
  // loose `.includes('xml')` in `isHtmlAttachmentPart` would otherwise
  // match, routing a zip container through the UTF-8 text reader.
  if (isDocxAttachmentPart(part)) {
    return false
  }

  if (isCodeAttachmentPart(part)) {
    return true
  }

  const mediaType = part.mediaType?.toLowerCase().trim() ?? ''
  if (mediaType.startsWith('text/')) {
    return true
  }
  if (TEXTUAL_APPLICATION_MIME_TYPES.has(mediaType)) {
    return true
  }
  // Some upstream MIME stamps include a `+suffix` (e.g. `application/foo+json`,
  // `application/something+xml`). Treat those as textual too.
  if (/\+(?:json|xml|yaml)$/i.test(mediaType)) {
    return true
  }

  if (urlLooksLikeTextDataUrl(part.url)) {
    return true
  }

  const extension = getExtension(part.filename)
  if (extension && TEXT_FILE_EXTENSIONS.has(extension)) {
    return true
  }

  return false
}

const EXTENSION_TO_FENCE_LANG: Record<string, string> = {
  eml: '',
  md: 'markdown',
  markdown: 'markdown',
  mdx: 'markdown',
  txt: '',
  log: '',
  csv: 'csv',
  tsv: 'tsv',
  psv: '',
  json: 'json',
  jsonl: 'json',
  jsonc: 'json',
  ndjson: 'json',
  yml: 'yaml',
  yaml: 'yaml',
  toml: 'toml',
  ini: 'ini',
  env: '',
  conf: '',
  cfg: 'ini',
  properties: 'ini',
  xml: 'xml',
  html: 'html',
  htm: 'html',
  mjml: 'xml',
  svg: 'xml',
  rss: 'xml',
  atom: 'xml',
  css: 'css',
  scss: 'scss',
  sass: 'sass',
  less: 'less',
  styl: 'stylus',
  postcss: 'css',
  ts: 'ts',
  tsx: 'tsx',
  js: 'js',
  jsx: 'jsx',
  mjs: 'js',
  cjs: 'js',
  vue: 'vue',
  svelte: 'svelte',
  astro: 'astro',
  py: 'python',
  pyi: 'python',
  rb: 'ruby',
  go: 'go',
  rs: 'rust',
  java: 'java',
  kt: 'kotlin',
  kts: 'kotlin',
  scala: 'scala',
  c: 'c',
  cc: 'cpp',
  cpp: 'cpp',
  cxx: 'cpp',
  h: 'c',
  hpp: 'cpp',
  hxx: 'cpp',
  m: 'objc',
  mm: 'objc',
  cs: 'csharp',
  fs: 'fsharp',
  vb: 'vb',
  php: 'php',
  swift: 'swift',
  dart: 'dart',
  lua: 'lua',
  pl: 'perl',
  pm: 'perl',
  sh: 'bash',
  bash: 'bash',
  zsh: 'bash',
  fish: 'bash',
  ps1: 'powershell',
  bat: 'bat',
  cmd: 'bat',
  sql: 'sql',
  gql: 'graphql',
  graphql: 'graphql',
  prisma: 'prisma',
  proto: 'protobuf',
  dockerfile: 'dockerfile',
  makefile: 'makefile',
  tf: 'hcl',
  hcl: 'hcl',
  nix: 'nix',
}

const MIME_TO_FENCE_LANG: Record<string, string> = {
  'message/rfc822': '',
  'text/mjml': 'xml',
  'text/jsx': 'jsx',
  'text/markdown': 'markdown',
  'text/csv': 'csv',
  'text/tab-separated-values': 'tsv',
  'text/html': 'html',
  'text/css': 'css',
  'text/xml': 'xml',
  'text/yaml': 'yaml',
  'text/x-yaml': 'yaml',
  'text/javascript': 'js',
  'text/typescript': 'ts',
  'text/x-python': 'python',
  'text/x-ruby': 'ruby',
  'text/x-shellscript': 'bash',
  'application/json': 'json',
  'application/jsonl': 'json',
  'application/ld+json': 'json',
  'application/x-ndjson': 'json',
  'application/xml': 'xml',
  'application/xhtml+xml': 'html',
  'application/yaml': 'yaml',
  'application/x-yaml': 'yaml',
  'application/javascript': 'js',
  'application/ecmascript': 'js',
  'application/typescript': 'ts',
  'application/x-typescript': 'ts',
  'application/sql': 'sql',
  'application/graphql': 'graphql',
  'application/toml': 'toml',
  'application/x-sh': 'bash',
  'application/x-shellscript': 'bash',
  'application/x-perl': 'perl',
  'application/x-python': 'python',
  'application/x-ruby': 'ruby',
}

/**
 * Best-effort markdown fence language hint for an attachment. Returns
 * `undefined` when no obvious match exists so the caller emits a plain
 * triple-backtick block.
 */
export function getMarkdownFenceLanguageForAttachment(
  part: AttachmentLikePart
): string | undefined {
  const mediaType = part.mediaType?.toLowerCase().trim() ?? ''
  if (mediaType && MIME_TO_FENCE_LANG[mediaType]) {
    return MIME_TO_FENCE_LANG[mediaType] || undefined
  }
  if (mediaType.startsWith('text/')) {
    const subtype = mediaType.slice('text/'.length)
    // text/markdown was already handled above, so this catches the long tail.
    if (subtype === 'plain') {
      return
    }
    if (subtype.endsWith('+xml')) {
      return 'xml'
    }
    if (subtype.endsWith('+json')) {
      return 'json'
    }
  }
  if (mediaType.endsWith('+json')) {
    return 'json'
  }
  if (mediaType.endsWith('+xml')) {
    return 'xml'
  }
  if (mediaType.endsWith('+yaml')) {
    return 'yaml'
  }

  const extension = getExtension(part.filename)
  if (extension && extension in EXTENSION_TO_FENCE_LANG) {
    const lang = EXTENSION_TO_FENCE_LANG[extension]
    return lang || undefined
  }
}

function decodeBase64Utf8(payload: string): string {
  const binary = atob(payload)
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}

function decodeTextDataUrl(url: string): string | undefined {
  const dataUrlMatch = url.match(
    /^data:([^;,]+)?(?:;charset=[^;,]+)?(;base64)?,([\s\S]*)$/i
  )
  if (!dataUrlMatch) {
    return
  }
  const [, , base64Flag, payload = ''] = dataUrlMatch
  try {
    return base64Flag ? decodeBase64Utf8(payload) : decodeURIComponent(payload)
  } catch {}
}

export type ReadTextAttachmentResult = {
  content: string
  truncated: boolean
  originalChars: number
}

export type ReadTextAttachmentOptions = {
  maxChars?: number
}

/**
 * Resolve the textual contents of an attachment part. Works for both
 * `data:` URLs and remote (Vercel Blob) URLs. The remote path uses the
 * project's shared `fetchWithRetry` so transient network blips do not
 * silently turn into "could not load contents" fallbacks.
 *
 * Returns `undefined` when the part is missing a URL, the fetch fails,
 * or the response body could not be decoded. The caller is expected to
 * fall back to a one-line summary in that case.
 */
export async function readTextAttachmentContent(
  part: AttachmentLikePart,
  fetchImpl: typeof fetch = fetchWithRetry,
  options?: ReadTextAttachmentOptions
): Promise<ReadTextAttachmentResult | undefined> {
  if (!part?.url || isRedactedInlineAttachmentUrl(part.url)) {
    return
  }

  let raw: string | undefined
  if (part.url.startsWith('data:')) {
    raw = decodeTextDataUrl(part.url)
  } else {
    try {
      const response = await fetchImpl(part.url)
      if (!response.ok) {
        return
      }
      raw = await response.text()
    } catch {
      return
    }
  }

  if (typeof raw !== 'string') {
    return
  }

  const originalChars = raw.length
  const maxChars = options?.maxChars ?? MAX_INLINED_TEXT_FILE_CHARS
  if (originalChars <= maxChars) {
    return { content: raw, truncated: false, originalChars }
  }
  return {
    content: raw.slice(0, maxChars),
    truncated: true,
    originalChars,
  }
}
